import { ArchiveIcon, ArchiveRestoreIcon, HashIcon, SearchIcon, UsersIcon } from 'lucide-react'
import { useState } from 'react'

import { errorMessage } from '@/api/client'
import { useConversations, useSearch, useSetConversationArchived } from '@/api/queries'
import type { ConversationSummary, ZoneSummary } from '@/api/types'
import { PageBody, PageHeader } from '@/components/AppShell'
import { RequireZone } from '@/components/RequireZone'
import { EmptyState, ErrorState, TableSkeleton } from '@/components/states'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { formatAbsolute, formatClock, pluralise } from '@/lib/format'
import { useReadOnly } from '@/staff/hooks'

/**
 * Archived conversations, on their own page (decision D-g).
 *
 * <h2>Why a separate page rather than a filter on the history</h2>
 * Decision 7 settled this as a <strong>hard split</strong>: two screens, two scopes, and no shared
 * "include archived" toggle. It keeps the messages page about live work, and it gives this page a
 * single clear purpose — which matters more than it looks, because this page's data is the part
 * destined to move to a slower store or to flat files. A page whose queries are already separate
 * can follow its data; a filter cannot.
 *
 * <h2>What archival is, and is not</h2>
 * A flag on the conversation and nothing else. Archiving writes one row and modifies no message
 * row, and it does <strong>not</strong> stop delivery: a queued message in an archived conversation
 * still arrives. Stopping delivery is pausing (§19), which is a different feature, and the console
 * must not blur them.
 *
 * <p>It also does not exempt anything from retention. Archival is a view concern and retention is a
 * storage concern; if archiving preserved rows past their horizon then "archive everything" would
 * be a free unlimited-storage tier.
 */
function ArchiveView({ zone }: { zone: ZoneSummary }) {
  const conversations = useConversations(zone.id, true)
  const [query, setQuery] = useState('')
  // `true` here and `false` on MessagesPage: the hard split, expressed at the only two call sites.
  const search = useSearch(zone.id, query, true)
  const readOnly = useReadOnly()
  const setArchived = useSetConversationArchived()

  const rows = conversations.data?.conversations ?? []
  const matched =
    query.trim().length > 0 && search.data !== undefined
      ? new Set(search.data.groups.map((group) => group.conversation_id))
      : undefined
  const visible = matched === undefined ? rows : rows.filter((row) => matched.has(row.id))

  function restore(conversation: ConversationSummary) {
    setArchived.mutate({ zoneId: zone.id, conversationId: conversation.id, archived: false })
  }

  return (
    <>
      <PageHeader
        title="Archive"
        description="Conversations you have put away. They are still delivered and still expire."
      />
      <PageBody>
        {setArchived.isError ? (
          <Alert variant="destructive">
            <AlertTitle>Could not change that conversation</AlertTitle>
            <AlertDescription>{errorMessage(setArchived.error)}</AlertDescription>
          </Alert>
        ) : null}

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ArchiveIcon className="size-4 text-primary" />
              Archived conversations
            </CardTitle>
            <CardDescription>
              Archiving is a view concern. An archived conversation still delivers its queued
              messages, and still expires when the zone&rsquo;s retention horizon reaches it.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="relative pb-3">
              <SearchIcon className="pointer-events-none absolute top-3 left-2.5 size-3.5 text-muted-foreground" />
              <input
                type="search"
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value)
                }}
                placeholder="Search archived messages"
                aria-label="Search archived messages"
                className="h-8 w-full rounded-md border border-border bg-background pr-2 pl-8 text-xs outline-none placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring"
              />
            </div>

            {conversations.isPending ? (
              <TableSkeleton rows={4} />
            ) : conversations.isError ? (
              <ErrorState
                error={conversations.error}
                title="Could not load the archive"
                onRetry={() => {
                  void conversations.refetch()
                }}
              />
            ) : rows.length === 0 ? (
              <EmptyState
                icon={ArchiveIcon}
                title="Nothing archived yet"
                description="Archive a conversation from the zone history to tidy it away without losing it."
              />
            ) : visible.length === 0 ? (
              <EmptyState
                icon={SearchIcon}
                title="No archived messages match"
                description="This search covers archived conversations only — the zone history has its own."
              />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Conversation</TableHead>
                    <TableHead>Span</TableHead>
                    <TableHead className="text-right">Messages</TableHead>
                    <TableHead className="w-px" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visible.map((conversation) => {
                    const channel = conversation.kind === 'CHANNEL'
                    return (
                      <TableRow key={conversation.id}>
                        <TableCell>
                          <span className="flex items-center gap-2">
                            {/* A detected channel segment and a direct thread are different kinds
                                of record; the icon is what makes that readable at a glance. */}
                            {channel ? (
                              <HashIcon className="size-3.5 text-muted-foreground" />
                            ) : (
                              <UsersIcon className="size-3.5 text-muted-foreground" />
                            )}
                            <span className="font-mono text-sm">
                              {channel
                                ? (conversation.label ?? 'channel')
                                : conversation.participants.join(', ')}
                            </span>
                            {channel ? <Badge variant="secondary">channel</Badge> : null}
                          </span>
                        </TableCell>
                        <TableCell
                          className="text-sm text-muted-foreground"
                          title={formatAbsolute(conversation.started_at)}
                        >
                          {formatClock(conversation.started_at)}
                          {' – '}
                          {/* Null ended_at means ongoing. An archived conversation CAN still be
                              open: archiving does not close it, and claiming otherwise would be
                              the console inventing a fact. */}
                          {conversation.ended_at === null
                            ? 'ongoing'
                            : formatClock(conversation.ended_at)}
                        </TableCell>
                        <TableCell className="text-right text-sm tabular-nums">
                          {conversation.message_count}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            hidden={readOnly}
                            disabled={!zone.reachable || setArchived.isPending}
                            onClick={() => {
                              restore(conversation)
                            }}
                          >
                            <ArchiveRestoreIcon className="size-3.5" />
                            Restore
                          </Button>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            )}

            {conversations.data?.has_more === true ? (
              <p className="pt-2 text-xs text-muted-foreground">
                Showing the most recent {pluralise(rows.length, 'conversation')}. Older ones exist.
              </p>
            ) : null}
          </CardContent>
        </Card>
      </PageBody>
    </>
  )
}

export function ArchivePage() {
  return <RequireZone>{(zone) => <ArchiveView zone={zone} />}</RequireZone>
}
